import '@theme/dialog';
import { unlockScroll } from '@theme/utilities';

const EU_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);

const STORAGE_PREFIX = 'tan-store-region-choice:v1';
const DETECTION_TIMEOUT_MS = 4000;

class TanStoreRegionGate extends HTMLElement {
  #dialogComponent;
  #dialog;
  #currentStoreUrl;
  #targetUrl;
  #storeRole;
  #storageKey;
  #sessionKey;
  #opened = false;
  #choiceRecorded = false;
  #abortController;

  connectedCallback() {
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#dialog = this.querySelector('dialog');
    this.#storeRole = this.dataset.storeRole;
    this.#currentStoreUrl = this.#parseCanonicalUrl(this.dataset.currentUrl);
    this.#targetUrl = this.#parseCanonicalUrl(this.dataset.targetUrl);

    const isDesignMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
    const currentStoreMatches = this.#currentStoreUrl?.origin === window.location.origin;
    const configuredOriginsDiffer = this.#currentStoreUrl?.origin !== this.#targetUrl?.origin;

    if (
      !this.#dialogComponent ||
      !this.#dialog ||
      !this.#currentStoreUrl ||
      !this.#targetUrl ||
      !configuredOriginsDiffer ||
      !this.#hasValidRole() ||
      (!currentStoreMatches && !isDesignMode)
    ) {
      return;
    }

    this.#storageKey = `${STORAGE_PREFIX}:${this.#storeRole}:${this.#targetUrl.hostname}`;
    this.#sessionKey = `${STORAGE_PREFIX}:scanned:${this.#storeRole}:${this.#targetUrl.hostname}`;

    this.querySelector('[data-region-close]')?.addEventListener('click', this.#handleDismiss);
    this.querySelector('[data-region-stay]')?.addEventListener('click', this.#handleStay);
    this.querySelector('[data-region-visit]')?.addEventListener('click', this.#handleVisit);
    this.querySelector('[data-region-visit]')?.addEventListener('auxclick', this.#handleVisitAux);
    this.#dialog.addEventListener('cancel', this.#handleNativeCancel);
    this.#dialog.addEventListener('close', this.#handleNativeClose);
    this.#dialogComponent.addEventListener('dialog:close', this.#handleDialogClose);

    window.setTimeout(() => this.#start(), 220);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.querySelector('[data-region-close]')?.removeEventListener('click', this.#handleDismiss);
    this.querySelector('[data-region-stay]')?.removeEventListener('click', this.#handleStay);
    this.querySelector('[data-region-visit]')?.removeEventListener('click', this.#handleVisit);
    this.querySelector('[data-region-visit]')?.removeEventListener('auxclick', this.#handleVisitAux);
    this.#dialog?.removeEventListener('cancel', this.#handleNativeCancel);
    this.#dialog?.removeEventListener('close', this.#handleNativeClose);
    this.#dialogComponent?.removeEventListener('dialog:close', this.#handleDialogClose);
  }

  async #start() {
    if (!this.isConnected) return;

    const isDesignMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
    const previewEnabled = this.dataset.preview === 'true';

    if (isDesignMode) {
      if (!previewEnabled) return;

      const previewCountry =
        this.#storeRole === 'europe'
          ? { code: 'CA', name: 'Canada' }
          : { code: 'FR', name: 'France' };
      this.#openForCountry(previewCountry, true);
      return;
    }

    if (!this.#storageAvailable()) return;

    if (this.#consumeArrivalMarker()) {
      this.#rememberChoice('arrived-from-recommended-store');
      return;
    }

    if (this.#rememberedState() !== 'none' || this.#sessionHasNonMatch()) return;

    const detectedCountry = await this.#detectCountry();
    if (!detectedCountry) {
      this.#rememberSessionNonMatch();
      return;
    }

    if (!this.#countryIsEligible(detectedCountry.code)) {
      this.#rememberSessionNonMatch();
      return;
    }

    this.#openForCountry(detectedCountry);
  }

  #parseCanonicalUrl(value) {
    try {
      const url = new URL(String(value || '').trim());
      return url.protocol === 'https:' && !url.username && !url.password ? url : null;
    } catch {
      return null;
    }
  }

  #hasValidRole() {
    return this.#storeRole === 'europe' || this.#storeRole === 'canada';
  }

  #countryIsEligible(countryCode) {
    if (this.#storeRole === 'europe') return countryCode === 'CA';
    return EU_COUNTRY_CODES.has(countryCode);
  }

  async #detectCountry() {
    this.#abortController = new AbortController();
    const timeout = window.setTimeout(() => this.#abortController?.abort(), DETECTION_TIMEOUT_MS);

    try {
      const root = window.Shopify?.routes?.root || '/';
      const endpoint = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
      if (endpoint.origin !== window.location.origin) return null;
      endpoint.searchParams.set('country[enabled]', 'true');

      const abortOnPageHide = () => this.#abortController?.abort();
      window.addEventListener('pagehide', abortOnPageHide, { once: true });

      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          redirect: 'error',
          headers: { Accept: 'application/json' },
          signal: this.#abortController.signal,
        });

        if (!response.ok) return null;

        const payload = await response.json();
        const country = payload?.detected_values?.country;
        const code = typeof country?.handle === 'string' ? country.handle.toUpperCase() : '';
        if (!/^[A-Z]{2}$/.test(code)) return null;

        return {
          code,
          name: typeof country.name === 'string' && country.name.trim() ? country.name.trim() : code,
        };
      } finally {
        window.removeEventListener('pagehide', abortOnPageHide);
      }
    } catch {
      // Fail closed: the active Shopify market is not a reliable GeoIP fallback.
      return null;
    } finally {
      window.clearTimeout(timeout);
      this.#abortController = undefined;
    }
  }

  #openForCountry(country, ignoreRememberedChoice = false) {
    if (
      this.#opened ||
      !this.isConnected ||
      (!ignoreRememberedChoice && this.#rememberedState() !== 'none')
    ) {
      return;
    }

    const values = {
      country: country.name,
      destination: this.dataset.destinationLabel || '',
      current: this.dataset.currentLabel || '',
    };

    this.#setText('[data-region-heading]', this.#formatTemplate(this.dataset.headingTemplate, values));
    this.#setText('[data-region-message]', this.#formatTemplate(this.dataset.messageTemplate, values));
    this.#setText('[data-region-visit-label]', this.#formatTemplate(this.dataset.visitTemplate, values));
    this.#setText('[data-region-stay-label]', this.#formatTemplate(this.dataset.stayTemplate, values));

    const visitLink = this.querySelector('[data-region-visit]');
    if (visitLink instanceof HTMLAnchorElement) visitLink.href = this.#destinationUrl().href;

    if (document.querySelector('dialog[open]')) return;

    this.#opened = true;
    this.#dialogComponent.showDialog();
  }

  #formatTemplate(template = '', values) {
    return template.replace(/\{(country|destination|current)\}/gi, (_, token) => values[token.toLowerCase()] || '');
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  #handleVisit = async (event) => {
    if (event instanceof MouseEvent && (event.altKey || event.button !== 0)) return;

    this.#rememberChoice('recommended-store');

    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey)) {
      return;
    }

    event.preventDefault();
    const destination = this.#destinationUrl();
    try {
      await this.#dialogComponent.closeDialog();
    } finally {
      window.location.assign(destination.href);
    }
  };

  #handleVisitAux = (event) => {
    if (event instanceof MouseEvent && event.button === 1) this.#rememberChoice('recommended-store');
  };

  #handleStay = () => {
    this.#rememberChoice('current-store');
    this.#dialogComponent.closeDialog();
  };

  #handleDismiss = () => {
    this.#rememberChoice('dismissed');
    this.#dialogComponent.closeDialog();
  };

  #handleNativeCancel = (event) => {
    event.preventDefault();
    if (!this.#dialog?.classList.contains('dialog-closing')) void this.#dialogComponent.closeDialog();
  };

  #handleNativeClose = () => {
    if (this.#dialog) unlockScroll(this.#dialog);
    if (this.#opened && !this.#choiceRecorded) this.#rememberChoice('dismissed');
    this.#opened = false;
  };

  #handleDialogClose = () => {
    if (this.#opened && !this.#choiceRecorded) this.#rememberChoice('dismissed');
    this.#opened = false;
  };

  #rememberChoice(action) {
    this.#choiceRecorded = true;
    const days = Math.min(30, Math.max(1, Number.parseInt(this.dataset.rememberDays || '14', 10) || 14));
    const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;

    try {
      localStorage.setItem(this.#storageKey, JSON.stringify({ action, expiresAt }));
    } catch {
      // Storage may be unavailable in restricted browsing modes; the choice still works for this page.
    }
  }

  #rememberedState() {
    try {
      const value = localStorage.getItem(this.#storageKey);
      if (value === null) return 'none';

      const stored = JSON.parse(value);
      if (!stored || typeof stored.expiresAt !== 'number') {
        localStorage.removeItem(this.#storageKey);
        return 'unavailable';
      }

      if (stored.expiresAt > Date.now()) return 'remembered';
      localStorage.removeItem(this.#storageKey);
      return 'none';
    } catch {
      try {
        localStorage.removeItem(this.#storageKey);
      } catch {
        // Keep the modal closed when preference storage cannot be trusted.
      }
      return 'unavailable';
    }
  }

  #storageAvailable() {
    const probeKey = `${this.#storageKey}:probe`;
    try {
      localStorage.setItem(probeKey, '1');
      localStorage.removeItem(probeKey);
      return true;
    } catch {
      return false;
    }
  }

  #sessionHasNonMatch() {
    try {
      return sessionStorage.getItem(this.#sessionKey) === 'true';
    } catch {
      return false;
    }
  }

  #rememberSessionNonMatch() {
    try {
      sessionStorage.setItem(this.#sessionKey, 'true');
    } catch {
      // This is only a same-session performance optimization.
    }
  }

  #destinationUrl() {
    const destination = new URL(this.#targetUrl.href);
    destination.searchParams.set('tan_region_arrival', `${this.#storeRole}-to-${this.#oppositeRole()}`);
    return destination;
  }

  #consumeArrivalMarker() {
    const currentUrl = new URL(window.location.href);
    const expectedMarker = `${this.#oppositeRole()}-to-${this.#storeRole}`;
    if (currentUrl.searchParams.get('tan_region_arrival') !== expectedMarker) return false;

    currentUrl.searchParams.delete('tan_region_arrival');
    try {
      history.replaceState(history.state, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    } catch {
      // A valid marker still suppresses the recommendation on this load.
    }
    return true;
  }

  #oppositeRole() {
    return this.#storeRole === 'europe' ? 'canada' : 'europe';
  }
}

if (!customElements.get('tan-store-region-gate')) {
  customElements.define('tan-store-region-gate', TanStoreRegionGate);
}
